import { Link } from "react-router-dom";
import BlogPostLayout from "./BlogPostLayout";
import { getPostBySlug } from "../../lib/blog";

const HOW_TO = {
  name: "Comment reconnaître un nid de frelons asiatiques ?",
  description: "Guide en 5 étapes pour identifier avec certitude un nid de frelons asiatiques (Vespa velutina) à distance en Creuse.",
  totalTime: "PT5M",
  steps: [
    { name: "Observer la forme du nid", text: "Un nid de frelons asiatiques est sphérique (comme un ballon de 30-80 cm), jamais ovoïde ou en cône." },
    { name: "Vérifier l'ouverture", text: "L'ouverture est TOUJOURS latérale, jamais par le bas. Une ouverture par le bas indique un nid de guêpes ou frelons européens." },
    { name: "Regarder la couleur", text: "Le nid a une couleur beige à marron clair, aspect papier mâché. Les nids gris clair sont ceux des guêpes." },
    { name: "Évaluer la hauteur", text: "Un nid secondaire de frelons asiatiques est perché haut (souvent 8-15 m) dans un arbre feuillu. Les nids bas près du sol sont plutôt européens." },
    { name: "Observer les allées-venues", text: "Observez à distance : si vous voyez des insectes sombres à pattes jaune vif entrer/sortir, c'est un frelon asiatique." },
  ],
};

export default function ReconnaitreNidFrelons() {
  const post = getPostBySlug("reconnaitre-nid-frelons-asiatiques-creuse");
  return (
    <BlogPostLayout post={post} howTo={HOW_TO}>
      <p>
        Depuis son arrivée en France en 2004, le <strong>frelon asiatique
        (Vespa velutina)</strong> a colonisé la Creuse et cause chaque année
        des dégâts sur les ruchers et des accidents graves. Savoir{" "}
        <strong>identifier un nid</strong> à distance et le signaler à temps
        est essentiel. Voici comment reconnaître un nid avec certitude.
      </p>

      <h2>Nid primaire vs nid secondaire</h2>
      <p>
        Un nid de frelons asiatiques passe par deux stades bien distincts au
        cours de l'année :
      </p>
      <ul>
        <li>
          <strong>Nid primaire (avril à juin)</strong> — de la taille d'une
          orange, généralement fixé sous un abri (cabanon, avancée de toit,
          garage). Une seule reine y démarre la colonie.
        </li>
        <li>
          <strong>Nid secondaire (juillet à novembre)</strong> — beaucoup plus
          gros (jusqu'à 80 cm de diamètre !), souvent perché à plus de 10 m
          dans un grand arbre. C'est le stade où la colonie devient dangereuse.
        </li>
      </ul>

      <h2>Les 5 critères pour identifier un nid</h2>
      <ol>
        <li>
          <strong>Forme sphérique ou piriforme (en poire)</strong>, jamais
          plate. Un nid de guêpes est ovoïde, un nid de frelons asiatiques a
          une forme plus ronde et enveloppée.
        </li>
        <li>
          <strong>Couleur beige clair à ocre</strong> — comme du carton mâché,
          avec des motifs concentriques visibles.
        </li>
        <li>
          <strong>Une seule entrée latérale</strong> (contrairement aux guêpes
          qui entrent par le bas). C'est un signe distinctif clé.
        </li>
        <li>
          <strong>Hauteur importante</strong> — 90 % des nids secondaires sont
          à plus de 8 m de haut, dans un arbre feuillu (chêne, tilleul,
          platane, robinier).
        </li>
        <li>
          <strong>Activité intense en vol stationnaire</strong> autour du nid,
          surtout si vous avez un rucher à proximité.
        </li>
      </ol>

      <div className="callout">
        <strong>Bon à savoir</strong>
        En automne, quand les feuilles tombent, les nids deviennent{" "}
        <strong>très visibles</strong>. C'est le moment idéal pour repérer les
        nids et les signaler avant l'hiver.
      </div>

      <h2>À ne pas confondre avec…</h2>
      <ul>
        <li>
          <strong>Un nid de frelons européens</strong> — souvent bas (creux
          d'arbre, cheminée), forme irrégulière, entrée en bas.
        </li>
        <li>
          <strong>Un nid de guêpes</strong> — plus petit (max 30 cm),
          ovoïde, souvent sous les toitures ou dans le sol.
        </li>
        <li>
          <strong>Un nid abandonné</strong> — pas d'activité, papier gris et
          effrité. Non dangereux mais à retirer pour éviter la confusion.
        </li>
      </ul>

      <h2>Vous avez repéré un nid : que faire ?</h2>
      <p>
        <strong>1. Ne vous approchez pas.</strong> Un nid actif contient
        plusieurs centaines de frelons. Une simple vibration peut déclencher
        une attaque.
      </p>
      <p>
        <strong>2. Prenez une photo depuis 5 m minimum</strong> (zoom).
        Utile pour le diagnostic à distance.
      </p>
      <p>
        <strong>3. Signalez à la mairie</strong> qui, en Creuse, participe
        souvent au coût de destruction dans certaines communes.
      </p>
      <p>
        <strong>4. Contactez un professionnel équipé</strong>. Voir notre page{" "}
        <Link to="/destruction-frelons-creuse">
          destruction de nids de frelons en Creuse
        </Link>{" "}
        pour les tarifs et modalités.
      </p>

      <div className="callout-warn">
        <strong>Ne détruisez jamais un nid vous-même</strong>
        Les piqûres multiples de frelons asiatiques peuvent provoquer un choc
        anaphylactique et, dans les cas les plus graves, le décès. C'est un
        travail de professionnel équipé.
      </div>

      <h2>Pour aller plus loin</h2>
      <p>
        Consultez également notre article{" "}
        <Link to="/conseils/frelons-asiatiques-creuse-que-faire">
          Frelons asiatiques en Creuse : que faire en cas de nid ?
        </Link>{" "}
        et notre page{" "}
        <Link to="/entreprise-nuisibles-creuse">
          entreprise anti-nuisibles Creuse
        </Link>{" "}
        pour tous nos services.
      </p>
    </BlogPostLayout>
  );
}
